import { Injectable, InjectionToken, inject } from '@angular/core';
import { Config } from './config';
import { HttpClient } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
	providedIn: 'root',
})
export class ConfigService {
	private http = inject(HttpClient);

	private config: Config;

	loadConfig() {
		// TODO Change config depending on environment
		return this.http
			.get('/config/' + this.getConfigFile())
			.pipe(
				catchError(() => of({})) // Handle error gracefully
			)
			.toPromise()
			.then((data: Config) => {
				this.config = data;
			});
	}

	getConfigFile(): string {
		switch (environment.name) {
			case 'STAGING':
				return 'config.stg.json';
			case 'PROD':
				return 'config.prod.json';
			case 'EPH':
				return 'config.eph.json';
			case 'LOCAL':
				return 'config.local.json';
			case 'DEV':
			default:
				return 'config.json';
		}
	}

	getConfig() {
		return this.config;
	}

	getProduction(): boolean {
		return this.config.production;
	}

	getLocal(): boolean {
		return this.config.local;
	}

	getVersion(): string {
		return this.config.version;
	}

	getKeycloakUrl(): string {
		return this.config.keycloakUrl;
	}

	getBaseUrl(): string {
		return this.config.baseUrl;
	}

	getRealEstateUrl(): string {
		return this.config.realEstateUrl;
	}

	getAuthUrl(): string {
		return this.config.authUrl;
	}

	getEnergyEfficiencyUrl(): string {
		return this.config.energyEfficiencyUrl;
	}

	getReportingUrl(): string {
		return this.config.reportingUrl;
	}

	getSettingsUrl(): string {
		return this.config.settingsUrl;
	}

	getPortalSettingUrl(): string {
		return this.config.portalSettingUrl;
	}

	getOutboxUrl(): string {
		return this.config.outboxUrl;
	}

	getDataExchangeUrl(): string {
		return this.config.dataExchangeUrl;
	}

	getJobLogUrl(): string {
		return this.config.jobLogUrl;
	}

	getArgeExchangeUrl(): string {
		return this.config.argeExchangeUrl;
	}

	getArasysExchangeUrl(): string {
		return this.config.arasysExchangeUrl;
	}

	getIssueTrackingUrl(): string {
		return this.config.issueTrackingUrl;
	}

	getArticleUrl(): string {
		return this.config.articleUrl;
	}

	getHardwareSecretUrl(): string {
		return this.config.hardwareSecretUrl;
	}

	getDeviceMessageUrl(): string {
		return this.config.deviceMessageUrl;
	}

	getReadingValueUrl(): string {
		return this.config.readingValueUrl;
	}

	getContactUrl(): string {
		return this.config.contactUrl;
	}

	getGatewaySetupUrl(): string {
		return this.config.gatewaySetupUrl;
	}

	getWorkOrderUrl(): string {
		return this.config.workOrderUrl;
	}

	getDeviceMonitoringUrl(): string {
		return this.config.deviceMonitoringUrl;
	}

	getDeviceStockUrl(): string {
		return this.config.deviceStockUrl;
	}

	getTariffBillingUrl(): string {
		return this.config.tariffBillingUrl;
	}

	getBillingUrl(): string {
		return this.config.billingUrl;
	}

	getLegacyAuthCodeUrl(): string {
		return this.config?.legacyAuthCodeUrl ?? '';
	}

	getDocumentHandlerUrl(): string {
		return this.config.documentHandlerUrl;
	}

	getWebsocketProtocol(): string {
		return this.config.websocketProtocol;
	}

	getKeycloakRealm(): string {
		return this.config.keycloakRealm;
	}

	getKeycloakClient(): string {
		return this.config.keycloakClient;
	}

	getObjectStoreUrl(): string {
		return this.config.objectStoreUrl;
	}

	getMailRelayUrl(): string {
		return this.config.mailRelayUrl;
	}

	getAraAppUrl(): string {
		return this.config.araAppUrl;
	}
}

export const APP_CONFIG: InjectionToken<Config> = new InjectionToken<Config>('ConfigService');
